import type { Image } from '@data-access/geospatial/imaging';
import { Quaternion, Vector2, Vector3 } from '@technology/web3d';
import { PlanarSettings } from '@technology/web3d-plugin-planar';
import { getLogger } from '@trimble/logging';
import {
  buildTilesUrlFromTemplateUrl,
  PanoramaImageStation,
  PlanarImageStation,
  StationsCapabilityProvider,
} from '@trimble/web3d-providers';
import type { ValidStations } from './station-images';

const logger = getLogger();

/**
 * Planes smaller than this sit on or behind a typical camera near clip, so
 * the plugin loads tiles but the mesh is not visible.
 */
const MIN_PLANAR_WORLD_METRES = 0.1;

interface InfeasiblePlanarStation {
  readonly id: string;
  readonly worldWidth: number;
  readonly worldHeight: number;
  readonly focalDistance: number;
  readonly pixelPitchX: number;
  readonly pixelPitchY: number;
}

function imageToPosition(img: Partial<Image>): Vector3 {
  return new Vector3(
    img.position?.x ?? 0,
    img.position?.y ?? 0,
    img.position?.z ?? 0
  );
}

function imageToQuaternion(img: Partial<Image>): Quaternion | undefined {
  const q = img.quaternionOrientation;
  if (!q) return undefined;
  return new Quaternion(q.x ?? 0, q.y ?? 0, q.z ?? 0, q.w ?? 0);
}

function imageToPlanarStation(
  img: Partial<Image>,
  runId: string,
  options: {
    allowEmptyTilesUrl?: boolean;
    onInfeasible?(info: InfeasiblePlanarStation): void;
  } = {}
): PlanarImageStation | null {
  if (!img.id) {
    return null;
  }
  const calibration = img.undistortedCalibration;
  if (
    calibration?.principalPointInPixels?.x === undefined ||
    calibration?.principalPointInPixels?.x === 0 ||
    calibration?.principalPointInPixels?.y === undefined ||
    calibration?.principalPointInPixels?.y === 0 ||
    calibration?.singlePixelDimensionInMetres?.x === undefined ||
    calibration?.singlePixelDimensionInMetres?.x === 0 ||
    calibration?.singlePixelDimensionInMetres?.y === undefined ||
    calibration?.singlePixelDimensionInMetres?.y === 0 ||
    calibration?.focalLengthInPixels?.x === undefined ||
    calibration?.focalLengthInPixels?.x === 0
  ) {
    return null;
  }
  const hasTemplateUrl = Boolean(img.tileset?.templateUrl);
  if (!options.allowEmptyTilesUrl && !hasTemplateUrl) {
    return null;
  }
  const tilesUrl = hasTemplateUrl
    ? buildTilesUrlFromTemplateUrl(img.tileset!.templateUrl!)
    : '';
  const position = imageToPosition(img);
  const quaternion = imageToQuaternion(img);
  const originalHeight = -(img.tileset?.boundingBox?.minY ?? 0);
  const originalWidth = img.tileset?.boundingBox?.maxX ?? 0;
  const pixelPitchX = calibration.singlePixelDimensionInMetres.x;
  const pixelPitchY = calibration.singlePixelDimensionInMetres.y;
  const principalPoint = new Vector2(
    calibration.principalPointInPixels.x * pixelPitchX,
    calibration.principalPointInPixels.y * pixelPitchY
  );
  const focalDistance = calibration.focalLengthInPixels.x * pixelPitchX;
  const worldHeight = originalHeight * pixelPitchY;
  const worldWidth = originalWidth * pixelPitchX;

  if (
    worldWidth < MIN_PLANAR_WORLD_METRES ||
    worldHeight < MIN_PLANAR_WORLD_METRES ||
    focalDistance < MIN_PLANAR_WORLD_METRES
  ) {
    options.onInfeasible?.({
      id: img.id,
      worldWidth,
      worldHeight,
      focalDistance,
      pixelPitchX,
      pixelPitchY,
    });
    return null;
  }

  return {
    kind: 'planar',
    runId,
    station: new PlanarSettings({
      id: img.id,
      tilesUrl,
      position,
      pixelHeight: originalHeight,
      worldHeight,
      worldWidth,
      quaternion,
      principalPoint,
      focalDistance,
      tileHeight: img.tileset?.tileHeight ?? 0,
      minFov: 1,
      minLodLevel: 2,
      lodAdjustment: 1,
      isUrlLevelZeroBased: true, // Imaging api planar tiles use 0-based URL levels (API does not send a flag).
    }),
    captureUtc: img.captureUtc ?? '',
  } satisfies PlanarImageStation;
}

function imageToPanoramaStation(
  img: Partial<Image>,
  runId: string
): PanoramaImageStation | null {
  if (!img.id) {
    return null;
  }
  const position = imageToPosition(img);
  const quaternion = imageToQuaternion(img);
  const tileset = img.tileset;
  return {
    kind: 'panorama',
    runId,
    station: {
      id: img.id,
      // Always store tileset metadata (dimensions, levels) so the batch URL
      // endpoint can later just patch in the templateUrl without needing the
      // full Image object. tilesetUrl is undefined when not yet resolved.
      tilesetInfo: tileset
        ? {
            tilesetUrl: tileset.templateUrl ?? undefined,
            tileWidth: tileset.tileWidth,
            tileHeight: tileset.tileHeight,
            resizedWidth: tileset.boundingBox?.maxX ?? 0,
            resizedHeight: -(tileset.boundingBox?.minY ?? 0),
            levels: tileset.levels?.length ?? 0,
            // Imaging default: 0-based. SDS mapper sets this from template layout.
            isUrlLevelZeroBased:
              'isUrlLevelZeroBased' in tileset
                ? (tileset as { isUrlLevelZeroBased: boolean })
                    .isUrlLevelZeroBased
                : true,
          }
        : undefined,
      position,
      quaternion: quaternion ?? new Quaternion(0, 0, 0, 0),
      captureUtc: img.captureUtc ?? '',
    },
  } satisfies PanoramaImageStation;
}

/**
 * Patches a template URL onto an existing station that was loaded without tileset URLs.
 * For planar: creates a new PlanarSettings with the resolved tilesUrl.
 * For panorama: clones the station with tilesetUrl set on tilesetInfo.
 * Returns null if the station type is unknown or the URL is empty.
 */
export function patchStationWithTemplateUrl(
  station: PlanarImageStation | PanoramaImageStation,
  templateUrl: string
): PlanarImageStation | PanoramaImageStation | null {
  if (!templateUrl) return null;

  if (station.kind === 'planar') {
    const tilesUrl = buildTilesUrlFromTemplateUrl(templateUrl);
    return {
      ...station,
      station: new PlanarSettings({
        ...station.station,
        tilesUrl,
      }),
    };
  }

  // Panorama: clone with tilesetUrl patched into existing tilesetInfo metadata
  return {
    ...station,
    station: {
      ...station.station,
      tilesetInfo: station.station.tilesetInfo
        ? { ...station.station.tilesetInfo, tilesetUrl: templateUrl }
        : undefined,
    },
  };
}

/**
 * Adds camera stations to the stations provider for both planar and panorama types.
 */
export function addCameraStationsToProvider(
  validStationsLists: ValidStations[],
  stationsProvider: StationsCapabilityProvider,
  options: { reportInfeasible?: boolean } = {}
): void {
  const reportInfeasible = options.reportInfeasible ?? true;
  const cameras = validStationsLists
    .map((validStations) => {
      const runId = validStations.runId;
      if (!validStations.isPanorama) {
        const infeasible: InfeasiblePlanarStation[] = [];
        const stationList = validStations.stations
          .map((station) =>
            imageToPlanarStation(station, runId, {
              allowEmptyTilesUrl: true,
              onInfeasible: (info) => infeasible.push(info),
            })
          )
          .filter((s): s is PlanarImageStation => s != null);

        // One warning per camera: a bad pixel pitch comes from ingest, so every
        // station of the camera carries the same infeasible value.
        if (reportInfeasible && infeasible.length > 0) {
          logger.warn(
            'Planar calibration builds a world plane below the camera near clip; stations skipped. Check the ingested pixel pitch unit.',
            {
              runId,
              cameraName: validStations.name,
              skippedStations: infeasible.length,
              totalStations: validStations.stations.length,
              minWorldMetres: MIN_PLANAR_WORLD_METRES,
              example: infeasible[0],
            }
          );
        }

        return {
          cameraName: validStations.name,
          kind: 'planar' as const,
          stationList,
        };
      } else {
        return {
          cameraName: validStations.name,
          kind: 'panorama' as const,
          stationList: validStations.stations
            .map((station) => imageToPanoramaStation(station, runId))
            .filter((s): s is PanoramaImageStation => s != null),
        };
      }
    })
    .filter((cam) => cam.stationList.length > 0);

  stationsProvider.addCameraStations(cameras);
}

/**
 * Station count for one run in isolation, using the same grouping as
 * {@link addCameraStationsToProvider} (panorama anchors, merged planars, orphan planars).
 */
export function computeStationCountForRun(
  validStationsLists: ValidStations[]
): number {
  if (validStationsLists.length === 0) {
    return 0;
  }
  const provider = new StationsCapabilityProvider();
  // Silent: callers add the same lists to the real provider, which already warns.
  addCameraStationsToProvider(validStationsLists, provider, {
    reportInfeasible: false,
  });
  return provider.stationCount();
}
